import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Link,
  IconButton,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SearchIcon from '@mui/icons-material/Search';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { starSymbol, unstarSymbol, getWatchedSet } from '../services/api';
import {
  getTickers,
  saveTickers,
  getWatchlist,
  saveWatchlist,
} from '../utils/portfolioStorage';
import { bloomberg } from '../theme/bloombergTheme';

interface TickerMenuProps {
  symbol: string;
  onAction?: () => void;
}

function useTickerMenu({ symbol, onAction }: TickerMenuProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [alertDialog, setAlertDialog] = useState(false);
  const [alertType, setAlertType] = useState<'price_above' | 'price_below'>('price_below');
  const [alertThreshold, setAlertThreshold] = useState('');

  const [newsDialog, setNewsDialog] = useState(false);
  const [newsItems, setNewsItems] = useState<any[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);

  const [watched, setWatched] = useState(false);
  useEffect(() => {
    if (anchorEl) getWatchedSet().then(set => setWatched(set.has(symbol.toUpperCase()))).catch(() => {});
  }, [anchorEl, symbol]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleStockPage = () => {
    handleClose();
    navigate(`/stock/${symbol}`);
    onAction?.();
  };

  const handleResearch = () => {
    handleClose();
    navigate(`/research?symbol=${symbol}`, { state: { from: 'ticker-menu' } });
    onAction?.();
  };

  const handleChart = () => {
    handleClose();
    navigate(`/charting?symbol=${symbol}`, { state: { from: 'ticker-menu' } });
    onAction?.();
  };

  const handleScreener = () => {
    handleClose();
    navigate(`/screener?lookup=${symbol}`, { state: { from: 'ticker-menu' } });
    onAction?.();
  };

  const handleAddToWatchlist = () => {
    handleClose();
    if (watched) {
      unstarSymbol(symbol).then(r => {
        setSnackbar({ open: true, message: r.status === 'unwatched'
          ? `${symbol} removed from watchlist`
          : `${symbol} still watched (in a model portfolio / tracker)` });
      }).catch(() => setSnackbar({ open: true, message: 'Failed to update watchlist', severity: 'error' }));
      const current = getWatchlist();
      saveWatchlist(current.filter(t => t !== symbol));
    } else {
      starSymbol(symbol).then(() => {
        setSnackbar({ open: true, message: `★ ${symbol} added to watchlist — now monitored` });
      }).catch(() => setSnackbar({ open: true, message: 'Failed to update watchlist', severity: 'error' }));
      const current = getWatchlist();
      if (!current.includes(symbol)) saveWatchlist([...current, symbol]);
    }
    onAction?.();
  };

  const handleAddToPortfolio = () => {
    handleClose();
    const current = getTickers();
    if (!current.includes(symbol)) {
      saveTickers([...current, symbol]);
      setSnackbar({ open: true, message: `${symbol} added to portfolio` });
    } else {
      setSnackbar({ open: true, message: `${symbol} already in portfolio` });
    }
    onAction?.();
  };

  const handleSetAlert = () => {
    handleClose();
    setAlertDialog(true);
  };

  const handleNews = async () => {
    handleClose();
    setNewsDialog(true);
    setNewsLoading(true);
    setNewsItems([]);
    try {
      const { data } = await api.get(`/api/stock/${symbol.toUpperCase()}/news`);
      setNewsItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setNewsItems([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!alertThreshold) return;
    try {
      await api.post(`/api/alerts`, {
        alert_type: alertType,
        symbol: symbol.toUpperCase(),
        threshold: parseFloat(alertThreshold),
        name: `${symbol} ${alertType === 'price_above' ? 'above' : 'below'} ${alertThreshold}`,
        is_recurring: false,
      });
      setSnackbar({ open: true, message: `Alert set for ${symbol}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Error creating alert', severity: 'error' });
    }
    setAlertDialog(false);
    setAlertThreshold('');
  };

  const menuContent = (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              bgcolor: '#171a21',
              border: '1px solid rgba(255,255,255,0.1)',
              '& .MuiMenuItem-root': {
                fontSize: '0.8rem',
                py: 0.75,
                '&:hover': { bgcolor: bloomberg.accent.orangeDim },
              },
              '& .MuiListItemIcon-root': { minWidth: 32 },
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: bloomberg.accent.orange, fontSize: '0.75rem' }}>
            {symbol}
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />
        <MenuItem onClick={handleStockPage}>
          <ListItemIcon><OpenInNewIcon sx={{ fontSize: 18, color: bloomberg.accent.orange }} /></ListItemIcon>
          <ListItemText>Stock Page</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleResearch}>
          <ListItemIcon><ArticleIcon sx={{ fontSize: 18, color: '#3b82f6' }} /></ListItemIcon>
          <ListItemText>View Research</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChart}>
          <ListItemIcon><ShowChartIcon sx={{ fontSize: 18, color: '#22d3ee' }} /></ListItemIcon>
          <ListItemText>View Chart</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleScreener}>
          <ListItemIcon><SearchIcon sx={{ fontSize: 18, color: bloomberg.text.secondary }} /></ListItemIcon>
          <ListItemText>Find Peers</ListItemText>
        </MenuItem>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />
        <MenuItem onClick={handleAddToWatchlist}>
          <ListItemIcon><BookmarkAddIcon sx={{ fontSize: 18, color: watched ? '#22c55e' : '#ffb347' }} /></ListItemIcon>
          <ListItemText>{watched ? '★ Watching — remove' : '★ Add to Watchlist'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddToPortfolio}>
          <ListItemIcon><PlaylistAddIcon sx={{ fontSize: 18, color: '#22c55e' }} /></ListItemIcon>
          <ListItemText>Add to Portfolio</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetAlert}>
          <ListItemIcon><AddAlertIcon sx={{ fontSize: 18, color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Set Price Alert</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNews}>
          <ListItemIcon><NewspaperIcon sx={{ fontSize: 18, color: '#a78bfa' }} /></ListItemIcon>
          <ListItemText>Latest News</ListItemText>
        </MenuItem>
      </Menu>
      <Dialog open={alertDialog} onClose={() => setAlertDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 14 }}>Set Price Alert — {symbol}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small">
              <InputLabel>Direction</InputLabel>
              <Select
                value={alertType}
                label="Direction"
                onChange={(e) => setAlertType(e.target.value as any)}
              >
                <MenuItem value="price_below">Price Below</MenuItem>
                <MenuItem value="price_above">Price Above</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Price"
              size="small"
              type="number"
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(e.target.value)}
              placeholder="e.g., 150.00"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialog(false)} size="small">Cancel</Button>
          <Button onClick={handleCreateAlert} variant="contained" size="small">Set Alert</Button>
        </DialogActions>
      </Dialog>
      {/* ── News Dialog ── */}
      <Dialog
        open={newsDialog}
        onClose={() => setNewsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0f1929', border: '1px solid rgba(255,255,255,0.08)', maxHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NewspaperIcon sx={{ fontSize: 18, color: '#a78bfa' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
              {symbol} — Latest News
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNewsDialog(false)} sx={{ color: bloomberg.text.secondary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {newsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
            </Box>
          ) : newsItems.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No news found for {symbol}</Typography>
            </Box>
          ) : (
            <Box>
              {newsItems.map((item, i) => {
                const ago = (() => {
                  try {
                    const diff = Date.now() - new Date(item.published_at).getTime();
                    const h = Math.floor(diff / 3600000);
                    if (h < 1) return `${Math.floor(diff / 60000)}m ago`;
                    if (h < 24) return `${h}h ago`;
                    return `${Math.floor(h / 24)}d ago`;
                  } catch { return ''; }
                })();
                const sentimentColor = item.sentiment === 'positive' ? '#4caf50' : item.sentiment === 'negative' ? '#ef4444' : '#94a3b8';
                return (
                  <Box
                    key={item.id ?? i}
                    sx={{
                      px: 2, py: 1.5,
                      borderBottom: i < newsItems.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                    }}
                  >
                    <Link
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: 'inherit' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.4, color: bloomberg.text.primary, mb: 0.5, '&:hover': { color: '#a78bfa' } }}>
                          {item.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '0.68rem', color: bloomberg.text.muted }}>{item.source}</Typography>
                          {ago && <Typography sx={{ fontSize: '0.68rem', color: '#475569' }}>· {ago}</Typography>}
                          {item.category && (
                            <Chip label={item.category} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.06)', color: bloomberg.text.secondary }} />
                          )}
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sentimentColor, flexShrink: 0 }} />
                        </Box>
                      </Box>
                      <OpenInNewIcon sx={{ fontSize: 14, color: '#475569', flexShrink: 0, mt: 0.25 }} />
                    </Link>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity || 'success'}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontSize: '0.8rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  return { handleOpen, menuContent, isOpen: Boolean(anchorEl) };
}

/** Renders a ticker as a clickable chip with context menu */
export const TickerChip: React.FC<{
  symbol: string;
  name?: string;
  size?: 'small' | 'medium';
  onAction?: () => void;
}> = ({ symbol, name, size = 'small', onAction }) => {
  const { handleOpen, menuContent } = useTickerMenu({ symbol, onAction });

  return (
    <>
      <Chip
        label={name ? `${symbol} - ${name}` : symbol}
        size={size}
        onClick={handleOpen}
        sx={{
          fontWeight: 700,
          fontSize: '0.7rem',
          cursor: 'pointer',
          color: bloomberg.accent.orange,
          borderColor: bloomberg.border.accent,
          '&:hover': {
            bgcolor: bloomberg.accent.orangeDim,
            borderColor: bloomberg.accent.orange,
          },
        }}
        variant="outlined"
      />
      {menuContent}
    </>
  );
};

/** Renders a ticker as clickable inline text with context menu */
export const TickerLink: React.FC<{
  symbol: string;
  fontWeight?: number;
  fontSize?: string | number;
  onAction?: () => void;
}> = ({ symbol, fontWeight = 700, fontSize = 'inherit', onAction }) => {
  const { handleOpen, menuContent } = useTickerMenu({ symbol, onAction });

  return (
    <>
      <Typography
        component="span"
        onClick={handleOpen}
        sx={{
          fontWeight,
          fontSize,
          cursor: 'pointer',
          color: bloomberg.accent.orange,
          transition: 'color 0.15s',
          '&:hover': {
            color: '#ffb347',
            textDecoration: 'underline',
          },
        }}
      >
        {symbol}
      </Typography>
      {menuContent}
    </>
  );
};

export default TickerChip;
